import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import projectService, { Inquiry } from '../../services/projectService';

type StatusType = Inquiry['status'] | string;

interface Props {
  open: boolean;
  inquiryId: string;
  initialStatus: StatusType;
  onClose: () => void;
  onUpdated?: () => void; // odświeżenie listy po zapisie/wysłaniu
}

const statusChip = (status: StatusType) => {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    'Przygotowane': { bg: '#E3F2FD', color: '#1976D2', label: 'Przygotowane' },
    'draft gmail': { bg: '#FFF8E1', color: '#FF8F00', label: 'Draft Gmail' },
    'Wysłane': { bg: '#E8F5E9', color: '#2E7D32', label: 'Wysłane' }
  };
  const s = map[status] || { bg: '#f5f5f5', color: '#000', label: String(status) };
  return (
    <Chip label={s.label} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 500 }} />
  );
};

export default function InquiryEmailDialog({ open, inquiryId, initialStatus, onClose, onUpdated }: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusType>(initialStatus);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [wysiwyg, setWysiwyg] = useState(true); // domyślnie podgląd/edycja jak w Gmailu
  const editableRef = useRef<HTMLDivElement | null>(null);
  const userEditingRef = useRef(false);

  const editable = status === 'Przygotowane';

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    projectService.getInquiryById(inquiryId)
      .then((inq) => {
        if (!mounted || !inq) return;
        setStatus(inq.status);
        setTo(inq.contractor_email);
        setSubject(inq.subject);
        const signature = (user as any)?.email_signature || '';
        const base = inq.description || '';
        const withSignature = signature && !base.includes(signature) ? `${base}<br><br>${signature}` : base;
        setBody(withSignature);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [open, inquiryId]);

  // Gdy przełączamy na WYSIWYG, ustaw wewnętrzny HTML jednorazowo, niech React go nie nadpisuje
  useEffect(() => {
    if (wysiwyg && editableRef.current && !userEditingRef.current) {
      editableRef.current.innerHTML = body || '';
    }
  }, [wysiwyg, open, body]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const currentHtml = wysiwyg ? (editableRef.current?.innerHTML || '') : body;
      // dołącz podpis użytkownika (jeśli dostępny) przy zapisie – tak jak przy wysyłce przez GmailService
      const signature = (user as any)?.email_signature || '';
      const finalBody = signature && !currentHtml.includes(signature) ? `${currentHtml}<br><br>${signature}` : currentHtml;
      await projectService.updateInquiry(inquiryId, {
        contractor_email: to,
        subject,
        description: finalBody
      });
      onUpdated && onUpdated();
    } finally { setLoading(false); }
  };

  const handleCreateDraft = async () => {
    setLoading(true);
    try {
      // Upewnij się, że zapisujemy ewentualne zmiany zanim utworzymy draft
      if (editable) {
        await handleSave();
      }
      const res = await api.post(`/inquiries/${inquiryId}/create-gmail-draft`);
      if (res.data?.success) {
        setStatus('draft gmail');
        onUpdated && onUpdated();
      }
    } finally { setLoading(false); }
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      if (status === 'Przygotowane') {
        // Zapisz ewentualne zmiany, a następnie wyślij bezpośrednio
        await handleSave();
        const res = await api.post(`/inquiries/${inquiryId}/send-direct-email`);
        if (res.data?.success) {
          setStatus('Wysłane');
          onUpdated && onUpdated();
        }
      } else if (status === 'draft gmail') {
        // Wyślij z draftu
        const inq = await projectService.getInquiryById(inquiryId);
        const draftId = inq?.gmail_draft_id;
        const res = await api.post(`/inquiries/${inquiryId}/send-gmail-draft`, { draftId });
        if (res.data?.success) {
          setStatus('Wysłane');
          onUpdated && onUpdated();
        }
      }
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Wiadomość e‑mail</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Status:</Typography>
            {statusChip(status)}
          </Grid>
          <Grid item xs={12}>
            <TextField label="Do" type="email" fullWidth size="small" value={to} onChange={(e) => setTo(e.target.value)} InputProps={{ readOnly: !editable }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Temat" fullWidth size="small" value={subject} onChange={(e) => setSubject(e.target.value)} InputProps={{ readOnly: !editable }} />
          </Grid>
          <Grid item xs={12}>
            {editable && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button size="small" onClick={() => setWysiwyg(!wysiwyg)}>
                  {wysiwyg ? 'Pokaż HTML' : 'Tryb edycji (WYSIWYG)'}
                </Button>
              </Box>
            )}
            {wysiwyg ? (
              <Box
                sx={{
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  p: 1.5,
                  minHeight: 200,
                  backgroundColor: '#fff',
                  '&:focus': { outline: 'none', borderColor: 'rgba(25,118,210,0.5)', boxShadow: '0 0 0 3px rgba(25,118,210,0.1)' }
                }}
                contentEditable={editable}
                suppressContentEditableWarning
                ref={editableRef}
                onInput={(e) => {
                  userEditingRef.current = true;
                  setBody((e.target as HTMLElement).innerHTML);
                  // Znieś flagę po klatce, aby efekt body-sync nie nadpisał caret
                  requestAnimationFrame(() => { userEditingRef.current = false; });
                }}
              />
            ) : (
              <TextField label="Treść (HTML)" fullWidth size="small" multiline minRows={8} value={body} onChange={(e) => setBody(e.target.value)} />
            )}
            {!editable && (
              <Typography variant="caption" color="text.secondary">Podgląd treści (tylko do odczytu dla statusu „Przygotowane” niedostępnego).</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Zamknij</Button>
        {editable && (
          <Button onClick={handleSave} disabled={loading} variant="outlined">Zapisz</Button>
        )}
        <Button onClick={handleCreateDraft} disabled={loading} variant="outlined">Draft</Button>
        <Button onClick={handleSend} disabled={loading} variant="contained">Wyślij</Button>
      </DialogActions>
    </Dialog>
  );
}


